import { useState, useEffect, useRef, useMemo } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Search, User, ShoppingCart, LogOut, Receipt } from 'lucide-react'
import StoreFilterBar from './StoreFilterBar'
import ItemsSortSelect from './ItemsSortSelect'
import CategoryChips from './CategoryChips'
import { useStorefrontCategories } from '@/hooks/useStorefrontCategories'
import type { InventoryItemNameRow } from '@/lib/api'
import { useInventoryCart } from '@/hooks/useInventoryCart'
import { itemMetaLine } from '@/lib/inventoryItemDisplay'

const AUTOCOMPLETE_MIN = 3
const SUGGESTION_CAP = 10
/** Flag stripe red (Old Glory red). */
const FLAG_STRIPE_RED = '#B22234'
/** Flag canton blue — left band of the title row only. */
const FLAG_FIELD_BLUE = '#3C3B6E'

type StoreHeaderProps = {
  /** Cached storefront inventory rows (same payload as store inventory lookup). */
  inventoryItems?: InventoryItemNameRow[]
  inventoryLoading?: boolean
}

// Fixed "random" values for falling stars (stable for SSR/hydration)
const FALLING_STARS = Array.from({ length: 36 }, (_, i) => ({
  left: ((i * 17 + 3) % 95) + 2,
  delay: (i * 0.4 + (i % 5) * 0.8) % 4,
  duration: 2.5 + (i % 7) * 0.5,
}))

export default function StoreHeader({
  inventoryItems = [],
  inventoryLoading = false,
}: StoreHeaderProps) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const isItemsRoute = pathname === '/items'
  const isCheckoutRoute = pathname === '/checkout'
  const isLoginRoute = pathname === '/login'
  const isOrderRoute = pathname.startsWith('/order/') || pathname === '/orders'
  const [searchQuery, setSearchQuery] = useState('')
  const [suggestOpen, setSuggestOpen] = useState(false)
  const searchWrapRef = useRef<HTMLDivElement>(null)
  const [accountOpen, setAccountOpen] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const accountRef = useRef<HTMLDivElement>(null)
  const { totalQty } = useInventoryCart()
  const { visibleCategories } = useStorefrontCategories(inventoryItems, inventoryLoading)
  const currentCategoryName = visibleCategories.find(
    (c) => c.category_id === searchParams.get('category')
  )?.name
  const pageLabel =
    searchParams.get('deals') === '1'
      ? 'Clearance & deals'
      : searchParams.get('favorites') === '1'
        ? 'My Favorite'
        : (currentCategoryName ?? 'All items')

  useEffect(() => {
    setIsLoggedIn(!!(typeof window !== 'undefined' && localStorage.getItem('srisai_access_token')))
    setIsAdmin(!!(typeof window !== 'undefined' && localStorage.getItem('srisai_admin_token')))
  }, [pathname])

  const itemFromUrl = searchParams.get('item') ?? ''

  useEffect(() => {
    setSearchQuery(itemFromUrl)
  }, [itemFromUrl])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) setAccountOpen(false)
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target as Node)) setSuggestOpen(false)
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  const suggestions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (q.length < AUTOCOMPLETE_MIN || inventoryLoading) return []
    return inventoryItems
      .filter(
        (row) =>
          row.item_name.toLowerCase().includes(q) || row.item_num.toLowerCase().includes(q)
      )
      .slice(0, SUGGESTION_CAP)
  }, [searchQuery, inventoryItems, inventoryLoading])

  const applyItemSearch = (value: string) => {
    const v = value.trim()
    if (!v) return
    const next = new URLSearchParams(searchParams)
    next.set('item', v)
    next.delete('page')
    // Deals scope isn't shown in the search box (category is), so a new search leaves it.
    next.delete('deals')
    navigate({ pathname: '/items', search: next.toString() })
    setSuggestOpen(false)
  }

  const handleSignOut = () => {
    localStorage.removeItem('srisai_access_token')
    localStorage.removeItem('srisai_admin_token')
    localStorage.removeItem('srisai_admin_user')
    setIsLoggedIn(false)
    setIsAdmin(false)
    setAccountOpen(false)
  }

  /** Category picker + item search, merged into one box (shared by home and /items). */
  const searchBox = (
    <div ref={searchWrapRef} className="relative min-w-0">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          applyItemSearch(searchQuery)
        }}
        className="flex h-9 min-w-0 items-stretch rounded-lg sm:h-11 sm:rounded-xl border-2 border-primary-100 bg-gray-50 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-100"
      >
        <StoreFilterBar inventoryItems={inventoryItems} inventoryLoading={inventoryLoading} />
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 sm:left-3" />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              setSuggestOpen(true)
            }}
            onFocus={() => setSuggestOpen(true)}
            placeholder="Search items…"
            aria-label="Search items"
            autoComplete="off"
            className="h-full w-full border-0 bg-transparent pl-8 pr-3 text-sm text-stone-900 outline-none sm:pl-10"
          />
        </div>
      </form>
      {suggestOpen && suggestions.length > 0 && (
        <ul
          className="absolute left-0 right-0 top-full z-[60] mt-1 max-h-72 overflow-y-auto rounded-xl border-2 border-primary-100 bg-white py-1 text-left shadow-lg"
          role="listbox"
        >
          {suggestions.map((row) => (
            <li key={row.inventory_id} role="option">
              <button
                type="button"
                className="w-full border-b border-primary-50 px-4 py-2.5 text-left last:border-0 hover:bg-primary-50"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => applyItemSearch(row.item_name)}
              >
                <span className="block whitespace-normal break-words font-medium text-gray-900">
                  {row.item_name || '(no name)'}
                </span>
                {itemMetaLine(row) && (
                  <span className="mt-0.5 block text-xs text-gray-500">
                    {itemMetaLine(row)}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )

  return (
    <>
      <header
        id="store-front-header"
        className="sticky top-0 z-50 border-b border-primary-100 bg-white shadow-sm overflow-x-clip"
      >
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Title row: full-viewport blue | red behind content only (not category/search). */}
          <div className="relative z-20 h-12 sm:h-20 md:h-24 lg:h-28">
            <div
              className="pointer-events-none absolute left-1/2 top-0 z-0 flex h-full w-screen -translate-x-1/2"
              aria-hidden
            >
              <div className="w-[25%] shrink-0" style={{ backgroundColor: FLAG_FIELD_BLUE }} />
              <div className="min-w-0 flex-1" style={{ backgroundColor: FLAG_STRIPE_RED }} />
            </div>
            <div
              className="pointer-events-none absolute left-1/2 top-0 z-[1] h-full w-screen -translate-x-1/2 overflow-hidden"
              aria-hidden
            >
              <div className="absolute left-0 top-0 h-full w-[25%] overflow-hidden">
                {FALLING_STARS.map((star, i) => (
                  <span
                    key={i}
                    className="absolute top-0 text-white animate-star-fall text-[10px] opacity-90"
                    style={{
                      left: `${star.left}%`,
                      animationDelay: `${star.delay}s`,
                      animationDuration: `${star.duration}s`,
                    }}
                  >
                    ★
                  </span>
                ))}
              </div>
            </div>
            <div className="relative z-10 flex h-full items-center justify-between gap-2 sm:gap-4">
            {/* Overlaps the blue | red boundary (25vw, full-bleed): margin reaches the boundary, img shifts back 20% of its width onto the blue. */}
            {/* Overlaps the blue | red boundary (25vw, full-bleed): margin reaches the boundary, img shifts back 20% of its width onto the blue. */}
            <Link
              to="/"
              className="flex h-full min-h-0 min-w-0 flex-1 items-center pr-1 sm:mr-4 sm:pr-0 ml-[calc(25vw_-_max(0px,(100vw_-_80rem)/2)_-_1rem)] sm:ml-[calc(25vw_-_max(0px,(100vw_-_80rem)/2)_-_1.5rem)] lg:ml-[calc(25vw_-_max(0px,(100vw_-_80rem)/2)_-_2rem)]"
            >
              {/* The PNG has wide white margins above and below the artwork; a 14:5 box with object-cover trims them. */}
              <img
                src="/ShreeSaiLogo.png"
                alt="Shree Sai Grocery - Fresh Produce"
                className="aspect-[14/5] h-11 w-auto max-w-none -translate-x-[20%] rounded object-cover sm:h-16 md:h-20 lg:h-24"
              />
            </Link>

            <div className="flex items-center gap-1 sm:gap-2 lg:gap-4 shrink-0">
              <Link
                to="/checkout"
                className="relative flex items-center gap-1.5 px-2 py-1.5 text-white/90 sm:gap-2 sm:px-3 sm:py-2 hover:text-white hover:bg-white/15 rounded-xl transition-colors"
                title={totalQty > 0 ? `Cart (${totalQty} items)` : 'Cart'}
                aria-label={totalQty > 0 ? `Cart, ${totalQty} items` : 'Cart, checkout'}
              >
                <ShoppingCart className="h-[18px] w-[18px] sm:h-5 sm:w-5" aria-hidden />
                {totalQty > 0 ? (
                  <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-400 px-1 text-[9px] sm:-right-0.5 sm:-top-0.5 sm:h-[1.125rem] sm:min-w-[1.125rem] sm:text-[10px] font-bold leading-none text-stone-900">
                    {totalQty > 99 ? '99+' : totalQty}
                  </span>
                ) : null}
                <span className="hidden lg:inline">Cart</span>
              </Link>
              <div className="relative" ref={accountRef}>
                {isLoggedIn ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setAccountOpen((o) => !o)}
                      aria-label="Account menu"
                      aria-haspopup="menu"
                      aria-expanded={accountOpen}
                      className="flex items-center gap-1.5 px-2 py-1.5 text-white/90 sm:gap-2 sm:px-3 sm:py-2 hover:text-white hover:bg-white/15 rounded-xl transition-colors"
                    >
                      <User className="h-[18px] w-[18px] sm:h-5 sm:w-5" />
                      <span className="hidden lg:inline">Account</span>
                    </button>
                    {accountOpen && (
                      <div className="absolute right-0 top-full mt-1 w-48 rounded-xl border border-primary-100 bg-white py-2 shadow-lg">
                        {isAdmin && (
                          <Link
                            to="/admin"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-primary-50"
                            onClick={() => setAccountOpen(false)}
                          >
                            Admin
                          </Link>
                        )}
                        <Link
                          to="/orders"
                          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-primary-50"
                          onClick={() => setAccountOpen(false)}
                        >
                          <Receipt className="w-4 h-4" />
                          My orders
                        </Link>
                        <button
                          type="button"
                          onClick={handleSignOut}
                          className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <LogOut className="w-4 h-4" />
                          Sign out
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to="/login"
                    className="flex items-center gap-1.5 px-2 py-1.5 text-white/90 sm:gap-2 sm:px-3 sm:py-2 hover:text-white hover:bg-white/15 rounded-xl transition-colors"
                  >
                    <User className="h-[18px] w-[18px] sm:h-5 sm:w-5" />
                    <span className="hidden lg:inline">Login</span>
                  </Link>
                )}
              </div>
            </div>
            </div>
          </div>

          {!isCheckoutRoute && !isLoginRoute && !isOrderRoute ? (
          <div className="relative z-10 border-t border-primary-100 bg-white py-1.5 sm:py-2">
            {isItemsRoute ? (
              <div className="flex min-w-0 flex-col gap-2 md:flex-row md:flex-wrap md:items-center md:gap-3">
                <div className="order-1 min-w-0 md:order-2 md:max-w-2xl md:flex-1">{searchBox}</div>
                <div className="order-2 flex items-center justify-between gap-2 md:contents">
                  <nav className="min-w-0 truncate text-sm text-stone-600 md:order-1 md:shrink-0" aria-label="Breadcrumb">
                    <Link
                      to="/"
                      className="font-medium text-stone-700 transition-colors hover:text-primary-600"
                    >
                      Home
                    </Link>
                    <span className="mx-1 text-stone-400 sm:mx-1.5" aria-hidden>
                      /
                    </span>
                    <span className="font-semibold text-stone-800" aria-current="page">
                      {pageLabel}
                    </span>
                  </nav>
                  <div className="shrink-0 md:order-3 md:ml-auto">
                    <ItemsSortSelect />
                  </div>
                </div>
                <div className="order-3 md:order-4 md:w-full">
                  <CategoryChips inventoryItems={inventoryItems} inventoryLoading={inventoryLoading} />
                </div>
              </div>
            ) : (
              <div className="max-w-2xl">{searchBox}</div>
            )}
          </div>
          ) : null}
        </div>
      </header>

    </>
  )
}
